// The car card's spec sheet, as data. Pure — no Phaser, no gameState — so `node --test` can
// check the wording and the arithmetic, and CarDetailsModal only has to draw the rows.
//
//   specRows(catalog.cars.panda)
//   → [{ icon: '💰', label: 'Cena', value: '8 000 zł' }, …]
//
// The reader is seven. Every row is one short Polish label and one short value: no units the
// child has to decode („l/100 km" is a ratio of a ratio), no engine codes, no decimals where
// a round number will do. „Diesel" beats „olej napędowy (ON)" and „Prąd 🔌" beats
// „elektryczny" for the same reason.

// Polish groups thousands with a non-breaking space. We do it by hand rather than with
// `toLocaleString('pl-PL')`, because CLDR only starts grouping at five digits there: the
// Panda's „8000 zł" and the B-class's „15 000 zł" then read as two different kinds of
// number, one row apart in the same table.
const NBSP = '\u00A0';

/** „8 000 zł” — whole złoty, grouped from a thousand up. */
export function zl(value) {
  const n = Math.round(Number(value) || 0);
  const grouped = String(Math.abs(n)).replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
  return `${n < 0 ? '-' : ''}${grouped} zł`;
}

/** Polish decimals use a comma: „5,0”. */
export function num(value, digits = 1) {
  return (Number(value) || 0).toFixed(digits).replace('.', ',');
}

/** How far a full tank (or battery) gets you, km. `capacity / consumption × 100`. */
export function rangeKm(spec) {
  const consumption = Number(spec?.consumption) || 0;
  if (consumption <= 0) return 0;
  return (Number(spec.capacity) || 0) / consumption * 100;
}

// What the child is actually choosing between at the pump. `ev` never visits a nozzle, so
// it says where its energy comes from instead.
const FUEL = {
  pb95: { name: 'Benzyna', tankIcon: '{canister}', tankLabel: 'Bak', unit: 'l' },
  on:   { name: 'Diesel',  tankIcon: '{canister}', tankLabel: 'Bak', unit: 'l' },
  ev:   { name: 'Prąd {plug}', tankIcon: '{battery}', tankLabel: 'Bateria', unit: 'kWh' },
};

// The paint, as something you can see next to the name rather than only read. Keyed by the
// server's `color_pl` (catalog.py CARS); anything unknown falls back to a neutral grey.
const SWATCH = {
  'szary': 0x9aa0a6,
  'czarny': 0x2b2b2b,
  'biały': 0xf4f4f4,
  'matowy ciemnoszary': 0x4a4f55,
  'czerwony': 0xd22a2a,
};
export const SWATCH_FALLBACK = 0xb0b0b0;

export function colorSwatch(colorPl) {
  return SWATCH[colorPl] ?? SWATCH_FALLBACK;
}

/**
 * The spec sheet as `[{ icon, label, value }]`, in reading order. `spec` is one entry of the
 * server catalog's `cars` map (or `school_car`, same keys).
 */
export function specRows(spec) {
  if (!spec) return [];
  const fuel = FUEL[spec.fuel] ?? FUEL.pb95;
  const ev = spec.fuel === 'ev';
  // Rounded to the nearest ten: „≈ 670 km" is the honest precision here (the real figure
  // moves with the weather) and it reads far faster than „673 km".
  const range = Math.round(rangeKm(spec) / 10) * 10;
  return [
    { icon: '{coin}', label: 'Cena', value: zl(spec.price) },
    { icon: '{fuel}', label: 'Paliwo', value: fuel.name },
    { icon: fuel.tankIcon, label: fuel.tankLabel, value: `${num(spec.capacity, 0)} ${fuel.unit}` },
    { icon: '{ruler}', label: 'Zasięg', value: `≈ ${range} km` },
    { icon: '{horse}', label: 'Moc', value: `${spec.hp} KM` },
    { icon: '{gear}', label: 'Biegi', value: ev ? 'automat (elektryczny)' : String(spec.gears ?? '—') },
    { icon: '{rocket}', label: 'Start 0–100', value: `${num(spec.accel_0_100_s ?? spec.accel_0_50_s * 3)} s` },
    { icon: '{finish}', label: 'Maks.', value: `${spec.vmax_kmh} km/h` },
    { icon: '{friends}', label: 'Miejsca', value: String(spec.seats) },
  ];
}
